type DocTarget = {
  slug: string;
  anchor?: string;
};

/**
 * Typed registry of documentation deep-link targets.
 */
export const DOC_KEY = {
  home: { slug: '' },
  'conversation.skills': { slug: 'agent/conversation/skills' },
  'knowledge-base': { slug: 'agent/knowledge-base' },
  workflows: { slug: 'engineering/workflows' },
  'code-remediation': { slug: 'engineering/code-remediation' },
  workbench: { slug: 'engineering/workbench' },
  'view-remediation': { slug: 'engineering/view-remediation' },
  'data-foundry': { slug: 'data/data-foundry' },
  'data-harmonization': { slug: 'data/data-harmonization' },
} as const satisfies Record<string, DocTarget>;

export type DocKey = keyof typeof DOC_KEY;

/**
 * Slug path of the authored Workflow nodes page. The registry's per-node pages
 * hang under it, so they have no `DOC_KEY` entry of their own.
 */
export const LOOM_NODES_SLUG_PATH = 'loom/nodes';

/**
 * Slug of the category section a node page hangs under. Categories arrive as
 * `NodeCategory` *values* ("Control Flow", "SAP"), which are already display
 * strings, so the slug is derived from the label.
 *
 * @param category - The node's category (`TLNodeSummary.category`).
 * @returns The category's path segment.
 * @example
 * loomNodeCategorySlug('Control Flow') // 'control-flow'
 */
export function loomNodeCategorySlug(category: string): string {
  return category.toLowerCase().replaceAll(' ', '-');
}

/**
 * Slug path of one workflow node's documentation page.
 *
 * @param nodeName - Registry name of the node (`TLNodeSummary.name`).
 * @param category - The node's category (`TLNodeSummary.category`), which names
 *   the section the page hangs under in the docs tree.
 * @returns The path to pass as `_splat` on the docs splat route.
 * @example
 * loomNodeDocSlug('http_request', 'Integrations') // 'loom/nodes/integrations/http_request'
 */
export function loomNodeDocSlug(nodeName: string, category: string): string {
  return `${LOOM_NODES_SLUG_PATH}/${loomNodeCategorySlug(category)}/${nodeName}`;
}

/**
 * Prefix authored content links use for a docs page, before the tenant is known.
 * MDX cross-page links are written root-relative against it (`/docs/data/sources`);
 * `tenantDocsHref` moves them onto the tenant route at render time.
 */
export const DOCS_AUTHORED_PREFIX = '/docs';

/**
 * Builds the in-app URL for a documentation slug path.
 *
 * @param slug - A docs slug path, e.g. `'agent/knowledge-base'`. Empty for the home page.
 * @param tenantId - The tenant whose docs to link to; the site lives under `/t/$tenantId`.
 * @returns The root-relative URL of the page.
 * @example
 * docUrl('agent/knowledge-base', 'acme') // '/t/acme/docs/agent/knowledge-base'
 * docUrl('', 'acme')                     // '/t/acme/docs'
 */
export function docUrl(slug: string, tenantId: string): string {
  const base = `/t/${encodeURIComponent(tenantId)}/docs`;
  return slug ? `${base}/${slug}` : base;
}

/**
 * Moves an authored `/docs/...` link onto the tenant-scoped docs route.
 *
 * @param href - A root-relative href as authored in MDX.
 * @param tenantId - The tenant whose docs to link to.
 * @returns The tenant-scoped href, or `href` unchanged when it does not address a docs page.
 * @example
 * tenantDocsHref('/docs/data/sources', 'acme') // '/t/acme/docs/data/sources'
 * tenantDocsHref('/docs', 'acme')              // '/t/acme/docs'
 * tenantDocsHref('/workbench', 'acme')         // '/workbench'
 */
export function tenantDocsHref(href: string, tenantId: string): string {
  if (href === DOCS_AUTHORED_PREFIX) return docUrl('', tenantId);
  if (!href.startsWith(`${DOCS_AUTHORED_PREFIX}/`)) return href;
  return docUrl(href.slice(DOCS_AUTHORED_PREFIX.length + 1), tenantId);
}

/**
 * Resolves a {@link DOC_KEY} entry to its in-app documentation URL.
 *
 * @param key - A registry key, e.g. `'conversation.skills'`.
 * @param tenantId - The tenant whose docs to link to.
 * @returns The URL, including a `#anchor` when the entry declares one.
 * @example
 * resolveDocUrl('conversation.skills', 'acme') // '/t/acme/docs/agent/conversation/skills'
 */
export function resolveDocUrl(key: DocKey, tenantId: string): string {
  const { slug, anchor }: DocTarget = DOC_KEY[key];
  return `${docUrl(slug, tenantId)}${anchor ? `#${anchor}` : ''}`;
}
